import React from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ServiceWorkOrderDto } from 'src/api/FiretrolApiClient';
import { serviceWorkOrderApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';
import { formatServiceJobLabel } from 'src/utils';

const MORE_RESULTS_VALUE = '__more_results__';

export interface ServiceWorkOrderOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface ServiceWorkOrderSearchState {
  data: ServiceWorkOrderOption[];
  onSearchChange: (term: string) => void;
  isFetching: boolean;
  nothingFoundMessage: string;
  isMoreMarker: (value: string | null | undefined) => boolean;
  resolveItem: (value: string) => ServiceWorkOrderDto | undefined;
}

const labelFor = (w: ServiceWorkOrderDto) =>
  formatServiceJobLabel({
    jobNumber: w.serviceCallId,
    address: w.adrscode,
    jobName: w.serviceDescription,
  });

/**
 * @registry Debounced service work-order search for the timesheet row picker;
 * preserves the saved selection when it falls outside the active result set.
 */
export function useServiceWorkOrderSearch(
  value?: string,
  snapshotLabel?: string,
  snapshotLocation?: string,
): ServiceWorkOrderSearchState {
  const [searchValue, setSearchValue] = React.useState('');
  const [debounced] = useDebouncedValue(searchValue, 300);

  const { data, isFetching, isError } = useQuery({
    queryKey: queryKeys.serviceWorkOrders.search(debounced),
    queryFn: ({ signal }) =>
      serviceWorkOrderApiClient.search(
        debounced || null,
        toCancelToken(signal),
      ),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    meta: { suppressErrorToast: true, errorContext: 'service call search' },
  });

  const results = React.useMemo(() => data?.items ?? [], [data]);
  const hasMore = data?.hasMore ?? false;

  const options = React.useMemo(() => {
    const base: ServiceWorkOrderOption[] = results.map((item) => ({
      value: item.serviceCallId,
      label: labelFor(item),
    }));
    if (value && !base.some((o) => o.value === value)) {
      base.unshift({
        value,
        label: formatServiceJobLabel({
          jobNumber: value,
          address: snapshotLocation,
          jobName: snapshotLabel,
        }),
      });
    }
    if (hasMore) {
      base.push({
        value: MORE_RESULTS_VALUE,
        label: 'Refine search to see more.',
        disabled: true,
      });
    }
    return base;
  }, [results, value, snapshotLabel, snapshotLocation, hasMore]);

  return {
    data: options,
    onSearchChange: setSearchValue,
    isFetching,
    nothingFoundMessage: isFetching
      ? 'Searching…'
      : isError
      ? 'Search failed. Try again.'
      : 'No service calls found',
    isMoreMarker: (v) => v === MORE_RESULTS_VALUE,
    resolveItem: (v) => results.find((item) => item.serviceCallId === v),
  };
}
